import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { SMSMockModal } from "react-common-ui-library";
import { useLocation, useNavigate } from "react-router";
import {
  ADD_OR_EDIT_VERIFICATION,
  OTP_VALIDATION_LIMIT_EXHAUSTED,
  EMAIL_ADDRESS,
  PHONE_NUMBER,
  IS_ACCOUNT_BLOCKED,
} from "../../Constants/VerifyAccountConstants";
import {
  CONSUMER_ACCOUNT_CREATION,
  PROVIDER_ACCOUNT_CREATION,
  LOG_IN,
  FORGOT_PASSWORD,
  EMPTY,
  LOGIN_ROUTE,
  ENABLE_ACCOUNT_ROUTE,
  SPAN,
  SMS_MOCK_OTP_ID,
  EMAIL,
  SECURITY_PREFERENCES_ROUTE,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  VERIFY_ACCOUNT_ROUTE,
} from "../../Constants/CommonConstants";
import { useSelector, useDispatch } from "react-redux";
import {
  clearLoginApiResponse,
  getAccountDetails,
  getVerificationMethods,
  postSendOTP,
  resetLoginMockSmsOtp,
} from "../../Features/LoginSlice";
import { VerifyAccountForm } from "./VerifyAccountForm";
import { CannotConfirmAccountModal } from "./Modals/CannotConfirmAccountModal";
import { AccountCreationFailedModal } from "../AccountCreation/Modals/AccountCreationFailedModal";
import { manageVerificationSelector } from "../../Selectors/VerificationSelector";
import { AlternateVerificationMethodLink } from "./AlternateVerificationMethodLink";
import { resetMockSmsOTP } from "../../Features/AccountCreationSlice";
import { VerifyAccountProgressAndBackBtn } from "./VerifyAccountProgressAndBackBtn";
import { VerifyAccountHeader } from "./VerifyAccountHeader";
import { useIntl } from "react-intl";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import {
  SLICE_LOGIN,
  THUNK_GET_VERIFICATION_METHODS,
} from "../../Constants/SliceAndThunkConstants";
import { performLogout } from "../../Features/AuthorizationSlice";

/**
 * @description Verify Account Page which verifies the user account through otp validation for the preferred verification method
 * @returns {ReactNode} Renders Verify Account screen to the user
 */

const VerifyAccount = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const navigate = useNavigate();

  const { previousScreen, method } = location.state;

  const [smsMockModalIsOpen, setSmsMockModalIsOpen] = useState(false);

  const {
    prefVerificationMethod,
    email,
    phoneNo,
    validateOTPDescription,
    mockSmsOTP,
    authToken,
  } = useSelector((state) => manageVerificationSelector(state, previousScreen));

  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState(
    prefVerificationMethod
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_LOGIN,
      THUNK_GET_VERIFICATION_METHODS
    )
  );
  const { isSuccess } = apiResponses;

  const [cannotConfirmModalIsOpen, setCannotConfirmModalIsOpen] =
    useState(false);

  const [
    accountCreationFailedModalIsOpen,
    setAccountCreationFailedModalIsOpen,
  ] = useState(false);

  useEffect(() => {
    if (
      sessionStorage.getItem(IS_ACCOUNT_BLOCKED) &&
      !cannotConfirmModalIsOpen &&
      location.pathname.includes(VERIFY_ACCOUNT_ROUTE)
    ) {
      dispatch(performLogout());
    }
  }, [dispatch, cannotConfirmModalIsOpen, location]);

  useEffect(() => {
    if (
      previousScreen === LOG_IN ||
      previousScreen === FORGOT_PASSWORD ||
      previousScreen === ENABLE_ACCOUNT_ROUTE
    ) {
      dispatch(getVerificationMethods());
    }
    if (previousScreen === ADD_OR_EDIT_VERIFICATION) {
      dispatch(getAccountDetails());
    }
    if (!previousScreen) {
      navigate(`/${LOGIN_ROUTE}`);
    }
  }, [dispatch, navigate, prefVerificationMethod, previousScreen]);

  useEffect(() => {
    if (
      isSuccess &&
      (previousScreen === LOG_IN ||
        previousScreen === FORGOT_PASSWORD ||
        previousScreen === ENABLE_ACCOUNT_ROUTE)
    ) {
      dispatch(postSendOTP({ otpType: prefVerificationMethod }));
      dispatch(clearLoginApiResponse(THUNK_GET_VERIFICATION_METHODS));
    }
  }, [dispatch, prefVerificationMethod, previousScreen, isSuccess]);

  useEffect(() => {
    if (validateOTPDescription === OTP_VALIDATION_LIMIT_EXHAUSTED) {
      switch (previousScreen) {
        case CONSUMER_ACCOUNT_CREATION:
        case PROVIDER_ACCOUNT_CREATION:
          setAccountCreationFailedModalIsOpen(true);
          break;
        case LOG_IN:
        case FORGOT_PASSWORD:
        case ENABLE_ACCOUNT_ROUTE:
        case ADD_OR_EDIT_VERIFICATION:
        case SECURITY_PREFERENCES_ROUTE:
          setCannotConfirmModalIsOpen(true);
          sessionStorage.setItem(IS_ACCOUNT_BLOCKED, true);
          break;
        default:
          break;
      }
    }
  }, [validateOTPDescription, previousScreen]);

  useEffect(() => {
    if (mockSmsOTP) {
      setSmsMockModalIsOpen(true);
    }
  }, [mockSmsOTP]);

  useEffect(() => {
    if (
      !authToken &&
      previousScreen === SECURITY_PREFERENCES_ROUTE &&
      !sessionStorage.getItem(IS_ACCOUNT_BLOCKED)
    ) {
      navigate(
        `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${SECURITY_PREFERENCES_ROUTE}`
      );
    }
  }, [authToken, navigate, previousScreen]);

  const smsMockModalCloseBtnClickHandler = () => {
    setSmsMockModalIsOpen(false);
    switch (previousScreen) {
      case CONSUMER_ACCOUNT_CREATION:
        dispatch(resetMockSmsOTP());
        break;
      case LOG_IN:
      case FORGOT_PASSWORD:
      case ADD_OR_EDIT_VERIFICATION:
      case ENABLE_ACCOUNT_ROUTE:
      case SECURITY_PREFERENCES_ROUTE:
        dispatch(resetLoginMockSmsOtp());
        break;
      default:
        break;
    }
  };

  return (
    <Stack spacing={{ xs: 4, md: 5 }}>
      <VerifyAccountProgressAndBackBtn
        previousScreen={previousScreen}
        selectedVerificationMethod={selectedVerificationMethod}
        method={method}
      />
      <Stack spacing={{ xs: 3, md: 2 }}>
        <VerifyAccountHeader
          selectedVerificationMethod={selectedVerificationMethod}
        />
        {email &&
        phoneNo &&
        previousScreen !== ADD_OR_EDIT_VERIFICATION &&
        previousScreen !== SECURITY_PREFERENCES_ROUTE ? (
          <AlternateVerificationMethodLink
            selectedVerificationMethod={selectedVerificationMethod}
            setSelectedVerificationMethod={setSelectedVerificationMethod}
            previousScreen={previousScreen}
          />
        ) : null}
      </Stack>
      <VerifyAccountForm
        previousScreen={previousScreen}
        selectedVerificationMethod={selectedVerificationMethod}
      />
      <CannotConfirmAccountModal
        open={cannotConfirmModalIsOpen}
        setCannotConfirmModalIsOpen={setCannotConfirmModalIsOpen}
      />
      <AccountCreationFailedModal
        open={accountCreationFailedModalIsOpen}
        setAccountCreationFailedModalIsOpen={
          setAccountCreationFailedModalIsOpen
        }
        contactInfo={intl.formatMessage({
          id:
            selectedVerificationMethod === EMAIL ? EMAIL_ADDRESS : PHONE_NUMBER,
        })}
      />
      <SMSMockModal
        open={smsMockModalIsOpen}
        otp={
          (
            <Box component={SPAN} id={SMS_MOCK_OTP_ID}>
              {mockSmsOTP}
            </Box>
          ) || EMPTY
        }
        closeButtonOnClick={smsMockModalCloseBtnClickHandler}
      />
    </Stack>
  );
};

export default VerifyAccount;
